import {
  AgencyBlocEntity,
  AgencyBlocPolicy,
  FeatureAuthorizationService,
  FormCheckboxComponent,
  FormSubmissionNames,
  FormSubmissionType,
  GenericAgencyBlocEntity,
  RoleName,
  SearchResults,
  SearchResultsRecord,
  SearchResultsRecordType,
} from '@agingplan';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgOption } from '@ng-select/ng-select';
import { BehaviorSubject, Observable, Subject, Subscription, catchError, filter, firstValueFrom, map } from 'rxjs';
import { PaginatedDataService } from 'src/app/pages/secure/components/paginated-data/paginated-data.service';
import { SidebarNotifications, SidebarService } from 'src/app/pages/secure/components/sidebar/sidebar.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { environment } from 'src/environments/environment';
import { AgencyBlocFields } from './components/agencybloc-fields/agencybloc-fields.component';
import { HealthRecord } from '../our-data/modals/record-details/pre-qualification/pre-qualification.component';
import { AssignTo } from './modals/assign-to/assign-to.component';
import { Wiki } from './modals/wiki/wiki.component';
import { PageFragment } from './page-fragment';

/**
 * Service responsible for managing data operations in the Our Data section
 * Handles CRUD operations, pagination, search, and synchronization with AgencyBloc
 */
@Injectable({
  providedIn: 'root',
})
export class OurDataService {
  /**
   * Categorized fields
   */
  public agencyBlocFields$ = new BehaviorSubject<AgencyBlocFields>({});

  /**
   * Categorized policy fields
   */
  public policyFields$ = new BehaviorSubject<AgencyBlocFields>({});

  /**
   * AgencyBloc fields visibility
   */
  public agency_bloc_fields_visible$ = new BehaviorSubject<boolean>(false);

  /**
   * Reconciliation delete id
   */
  public reconciliation_delete_id$ = new BehaviorSubject<number | null>(null);

  /**
   * The values to use when syncing the entity with AgencyBloc.
   */
  public partialEntityValues?: PartialEntityValues<Partial<SearchResultsRecord>>;

  /**
   * The data for the current page and fragment
   */
  public pageFragment$ = new BehaviorSubject<PageFragment | undefined>(undefined);

  /**
   * Total Pagination data
   */
  public all_pagination_options$ = new BehaviorSubject<NgOption[]>([]);

  /**
   * The current record being viewed
   */
  public record$ = new BehaviorSubject<SearchResultsRecord | undefined>(undefined);

  /**
   * The batch of records to perform batch actions on
   */
  public batchTo$ = new BehaviorSubject<number[]>([]);

  /**
   * Checked records
   */
  public selectedRecords$ = new BehaviorSubject<SearchResultsRecord[]>([]);
  public selectedHealthHistory$ = new BehaviorSubject<HealthRecord[]>([]);

  /**
   * The batch of records to perform batch actions on
   */
  public ltci_options$ = new BehaviorSubject<NgOption[]>([]);

  /**
   * A model to attach to the 'Check All Records' form-checkbox
   */
  public checkbox = false;

  /**
   * check if any checkbox is selected from records data table
   */
  public is_any_checkbox_selected$ = new BehaviorSubject<boolean>(false);

  /**
   * A boolean indicating whether the user wants to permanently delete records
   */
  public permanent_delete$ = new BehaviorSubject<boolean>(false);

  /**
   * move selected ids to underwritting
   */
  public is_under_writting_processing$ = new BehaviorSubject<boolean>(false);

  /**
   * selected agent id for get paginated data
   */
  public selected_agent_id$ = new BehaviorSubject<number | null | undefined>(null);

  /**
   * action type
   */
  public action_type$ = new BehaviorSubject<string>('batch');

  /**
   * Can the User edit or view record data.
   */
  public accessable$ = new BehaviorSubject<{ edit: boolean; view: boolean }>({ edit: false, view: false });

  /**
   * Observable that tracks the state of the "not authorized" modal.
   * Emits `true` when the modal should be open, and `false` when it should be closed.
   */
  public is_not_authorized_modal_open$ = new BehaviorSubject<boolean>(false);

  /**
   * wolf archive
   */
  public wolf_archived$ = new BehaviorSubject<boolean>(false);

  /**
   * archive for agent
   */
  public agent_archived$ = new BehaviorSubject<boolean>(false);

  /**
   * Open pre under writting email modal
   */
  public open_pre_under_writting_modal$ = new BehaviorSubject<boolean>(false);

  /**
   * Similar records to the current one
   */
  public similarRecords: SearchResultsRecord[] = [];

  /**
   * All of the data to display
   */
  public pagination$ = this.paginatedDataService.pagination$;

  /**
   * Opens the reusable modal to the given `TemplateRef<any>`'s name
   */
  public presentByName$ = new Subject<string>();

  /**
   * Emits when the create record finished
   */
  public closedComplete$ = new BehaviorSubject<boolean>(false);

  /**
   * Employees have additional actions available
   */
  public get ifEmployee(): boolean {
    return this.authenticationService.user.value?.employee ?? false;
  }

  /**
   * Returns true if the current user is an administrator
   */
  public get ifAdministrator(): boolean {
    return this.authenticationService.user.value?.administrator ?? false;
  }

  /**
   * Returns true if the current user is a super administrator
   *
   * This is a shortcut to check if the user has the
   * {@link RoleName.SUPER_ADMINISTRATOR} role
   *
   * @returns {boolean} - True if the user is a super administrator, false otherwise
   */
  public get ifSuperAdministrator(): boolean {
    return this.authenticationService.user?.value?.hasRole(RoleName.SUPER_ADMINISTRATOR) ?? false;
  }

  /**
   * The drop down option for batch
   */
  public batch_options: NgOption[] = [
    { value: 'batch', label: 'BATCH EMAIL' },
    { value: 'assign', label: 'ASSIGN' },
    { value: 'archive', label: 'ARCHIVE' },
    { value: 'restore_archive', label: 'RESTORE ARCHIVE' },
    { value: 'delete', label: 'DELETE' },
  ];

  /**
   * page name
   */
  public page_name = '';

  /**
   * Active subscriptions for the our-data route
   */
  private activeSubscriptions: Subscription[] = [];

  /**
   * Router subscription to manage other subscriptions
   */
  private routerSubscription: Subscription;

  constructor(
    public sidebarService: SidebarService,
    private http: HttpClient,
    private navigationService: NavigationService,
    private authenticationService: AuthenticationService,
    private paginatedDataService: PaginatedDataService,
    private route: ActivatedRoute,
    private router: Router,
    private featureAuthorizationService: FeatureAuthorizationService,
  ) {
    // Subscribe to router events to manage subscriptions
    this.routerSubscription = this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      if (event.urlAfterRedirects.includes('/our-data/')) {
        this.initializeSubscriptions();
      } else {
        this.cleanupSubscriptions();
      }
    });

    // Check initial route
    if (this.router.url.includes('/our-data/')) {
      this.initializeSubscriptions();
    }
  }

  /**
   * Initialize subscriptions for the our-data route
   */
  private initializeSubscriptions(): void {
    // Cleanup any existing subscriptions first
    this.cleanupSubscriptions();

    // Subscribe to record changes
    this.activeSubscriptions.push(
      this.record$.subscribe((record) => {
        if (!record) {
          this.partialEntityValues = undefined;
        }
      }),
    );

    // Subscribe to pagination changes
    this.activeSubscriptions.push(
      this.pagination$.subscribe((records) => {
        this.batchTo$.next(records?.data.filter((record) => record.checked).map((record) => record.id) ?? []);
      }),
    );

    // Subscribe to router events for page fragment updates
    this.activeSubscriptions.push(
      this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
        this.refreshPageFragmentData(event.urlAfterRedirects);
      }),
    );

    // Refresh page fragment data
    this.refreshPageFragmentData(this.router.url);

    // Update LTCI options
    this.updateLTCIOption();
  }

  /**
   * Cleanup active subscriptions and reset state
   */
  private cleanupSubscriptions(): void {
    // Unsubscribe from all active subscriptions
    this.activeSubscriptions.forEach((sub) => sub.unsubscribe());
    this.activeSubscriptions = [];

    // Reset state
    this.reset();
  }

  /**
   * Cleanup router subscription if needed
   */
  public cleanupRouterSubscription(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.cleanupSubscriptions();
  }

  /**
   * Reset all state in the service
   * Call this when navigating away or when state needs to be reset
   */
  public reset(): void {
    this.agencyBlocFields$.next({});
    this.policyFields$.next({});
    this.agency_bloc_fields_visible$.next(false);
    this.reconciliation_delete_id$.next(null);
    this.pageFragment$.next(undefined);
    this.all_pagination_options$.next([]);
    this.record$.next(undefined);
    this.batchTo$.next([]);
    this.is_any_checkbox_selected$.next(false);
    this.is_under_writting_processing$.next(false);
    this.selected_agent_id$.next(null);
    this.action_type$.next('batch');
    this.accessable$.next({ edit: false, view: false });
    this.is_not_authorized_modal_open$.next(false);
    this.wolf_archived$.next(false);
    this.agent_archived$.next(false);
    this.open_pre_under_writting_modal$.next(false);
    this.similarRecords = [];
    this.checkbox = false;
    this.partialEntityValues = undefined;
  }

  /**
   * Refreshes the page fragment data based on the provided URL.
   * @param {string} url - The URL to extract the page name and fragment from.
   */
  public async refreshPageFragmentData(url: string) {
    if (!url.includes('our-data')) return;
    this.checkUserPermission(url);
    const { pageName, fragment } = this.extractPageNameAndFragment(url);
    const urlSegment = this.getFormattedStringFromUrl(url);
    const accessUrl: string[] = [];
    if (urlSegment[1] !== pageName) {
      const accessEditUrl = `${urlSegment[0]}|${urlSegment[1]}-${pageName}-edit`;
      const accessViewUrl = `${urlSegment[0]}|${urlSegment[1]}-${pageName}-view`;
      accessUrl.push(accessEditUrl);
      accessUrl.push(accessViewUrl);
    } else {
      const accessEditUrl = `${urlSegment[0]}|${pageName}-edit`;
      const accessViewUrl = `${urlSegment[0]}|${pageName}-view`;
      accessUrl.push(accessEditUrl);
      accessUrl.push(accessViewUrl);
    }

    if (
      typeof pageName === 'string' &&
      pageName.trim() !== '' &&
      typeof fragment === 'string' &&
      fragment.trim() !== '' &&
      accessUrl.some((feature) => this.featureAuthorizationService.canAccessFeature(feature))
    ) {
      const data = await this.getPageFragmentData(pageName, fragment);
      data.fields = data.fields?.map((field) => {
        if (field.fragment) field.enabled = field.fragment.includes(fragment);
        else field.enabled = false;
        return field;
      });
      await this.getPaginationFromCursor(data, data.fragment);
      this.pageFragment$.next(data);
    }
  }

  /**
   * Extracts and formats the path segments from a given URL.
   *
   * @param url - The URL string to be processed.
   * @returns An array of strings representing the path segments of the URL.
   */
  public getFormattedStringFromUrl(url: string): string[] {
    const [path, fragment] = url.replace(/^\//, '').split('#');
    const pathSegments = path.split('/');
    return pathSegments;
  }

  /**
   * Generates and sets batch options based on the provided page name and archived status.
   *
   * @param pageName - The name of the page to determine specific batch options.
   * @param archived - A boolean indicating whether the items are archived or not.
   *
   * The method sets the `batch_options` array with different options:
   * - If the page name is not 'reconciliation', it includes 'BATCH EMAIL', 'ASSIGN', and 'DELETE'.
   * - If the items are archived, it adds 'RESTORE ARCHIVE'.
   * - If the items are not archived and the page name is not 'reconciliation', it adds 'ARCHIVE'.
   * - If the items are not archived and the page name is 'reconciliation', it adds 'DELETE'.
   */
  private getBatchOption(pageName1: string, archived: boolean | undefined) {
    const { pageName, fragment } = this.extractPageNameAndFragment(this.router.url);
    let batch_options: NgOption[] = [];
    if (pageName !== 'reconciliation') {
      batch_options = [
        { value: 'batch', label: 'BATCH EMAIL' },
        { value: 'assign', label: 'ASSIGN' },
        { value: 'delete', label: 'DELETE' },
        { value: 'delete_permanently', label: 'DELETE FOREVER' },
      ];
      if (this.ifSuperAdministrator && !batch_options.some((opt) => opt.value === 'delete_permanently')) {
        batch_options.push({ value: 'delete_permanently', label: 'DELETE FOREVER' });
      }
    }
    if (archived) {
      if (!batch_options.some((opt) => opt.value === 'restore_archive')) {
        batch_options.push({ value: 'restore_archive', label: 'RESTORE ARCHIVE' });
      }
    } else {
      if (pageName !== 'reconciliation') {
        if (!batch_options.some((opt) => opt.value === 'archive')) {
          batch_options.push({ value: 'archive', label: 'ARCHIVE' });
        }
      } else {
        if (!batch_options.some((opt) => opt.value === 'delete')) {
          batch_options.push({ value: 'delete', label: 'DELETE' });
        }
      }
    }

    if (fragment === 'health_questionnaire') {
      if (!batch_options.some((opt) => opt.value === 'pre_underwriting_email')) {
        batch_options.push({ value: 'pre_underwriting_email', label: 'PRE UNDERWRITING EMAIL' });
      }
    } else {
      if (batch_options.some((opt) => opt.value === 'pre_underwriting_email')) {
        batch_options = batch_options.filter((opt) => opt.value !== 'pre_underwriting_email');
      }
    }

    this.batch_options = batch_options;
  }

  /**
   * extract page and fragment from url
   * @param {string} url
   * @return { pageName: string, fragment: string }
   */
  public extractPageNameAndFragment(url: string): { pageName: string; fragment: string } {
    // Remove leading and trailing slashes
    url = url.replace(/^\/+|\/+$/g, '');

    // Split the URL by '#' to separate the fragment
    const parts = url.split('#');

    // Get the last segment of the path as the page name
    const segments = parts[0].split('/');
    const pageName = segments[segments.length - 1];

    this.page_name = pageName;
    // Fragment value (if present)
    const fragment = parts[1] || '';

    return { pageName, fragment };
  }

  /**
   * Check if user have edit or view permission
   * @param url
   */
  private checkUserPermission(url: string) {
    const { pageName, fragment } = this.extractPageNameAndFragment(url);
    const url_items = url.split('/');
    const filteredArr = url_items.filter((item) => item !== '' && !item.includes('#') && !item.includes('our-data'));
    filteredArr.push(pageName);
    const current_page = filteredArr.join('-');

    //Check if can edit
    const can_edit = [`our-data|${current_page}-edit`].every((feature) => this.featureAuthorizationService.canAccessFeature(feature)) ?? false;
    //Check if can view
    const can_view = [`our-data|${current_page}-view`].every((feature) => this.featureAuthorizationService.canAccessFeature(feature)) ?? false;
    //Update to Observer
    this.accessable$.next({ edit: can_edit, view: can_view });
  }

  /**
   * Get the data for the given fragment
   * @param {string} fragment - The fragment to get the data for
   */
  public async getPaginationFromCursor(path?: PageFragment, fragment?: string, parameters = this.paginatedDataService.options, reloading = true) {
    // If the path is not provided, use the current page fragment
    path = path ?? this.pageFragment$.value;
    // If the fragment is not provided, use the current fragment
    fragment = path?.fragment || fragment;

    const master_page = path?.page || '';

    // Update the route to include the fragment if the fragment is not in the route without reloading the page
    if (!fragment) {
      await this.router.navigate([], { relativeTo: this.route, fragment, replaceUrl: true });
    } else if (this.route.snapshot.fragment !== fragment) {
      await this.router.navigate([], { relativeTo: this.route, fragment, replaceUrl: true });
    }

    // Show the loading spinner
    if (reloading) this.navigationService.addToWorkQueue();
    let data_url = 'our-data';
    if (path?.page === 'logs') {
      data_url = 'activities';
      parameters = { ...this.paginatedDataService.log_options, form: fragment };
    } else {
      // Update the parameters
      parameters = { ...parameters, form: fragment || 'agents', scheduling: this.router.url.includes('scheduling'), master_page: master_page };
    }

    //Add agency_bloc_agent_id to parameters
    if (this.selected_agent_id$.value) {
      parameters = { ...parameters, agency_bloc_agent_id: this.selected_agent_id$.value, archived: true };
    }

    this.getBatchOption(this.page_name, parameters?.archived);

    // Get the data
    return await firstValueFrom(this.paginatedDataService.getPageAtCursor<SearchResultsRecordType>(`${environment.api_url}/${data_url}`, parameters))
      .then((pagination) => {
        // Set the pagination data
        pagination.data = pagination.data.map((record) => SearchResultsRecord.factory(record));

        // Update the pagination
        this.paginatedDataService.pagination$.next(pagination);

        // update all pagination options data
        const all_pagination_options = pagination.data.map((record) => {
          return { value: record.id, label: record.full_name };
        });
        this.all_pagination_options$.next(all_pagination_options);
        return pagination;
      })
      .then(() => {
        // Update the notifications
        this.getNotifications().then((notifications) => {
          this.sidebarService.notifications$.next(notifications);
        });
      })
      .finally(() => {
        if (reloading) this.goIdle();
      });
  }

  /**
   * Get the notifications for the current user
   * @returns {Promise<SidebarNotifications>}
   */
  private async getNotifications() {
    return await firstValueFrom(this.http.get<SidebarNotifications>(`${environment.api_url}/our-data/notifications`));
  }

  /**
   * Get the data for the current page and fragment
   * @param {string} page - The page to get the data for
   * @param {string} fragment - The fragment to get the data for
   * @returns {Promise<PageFragment>}
   */
  public async getPageFragmentData(page: string, fragment: string): Promise<PageFragment> {
    // Set the working flag
    this.navigationService.addToWorkQueue();

    // If the current url includes 'scheduling', set the scheduling flag
    const scheduling = this.router.url.includes('scheduling');

    return await firstValueFrom(
      this.http.get<PageFragment>(`${environment.api_url}/agencybloc/data/${page}/${fragment}?scheduling=${scheduling}`).pipe(
        map((data) => {
          return {
            page,
            fragment,
            fields: data.fields,
            parsed: {},
            wiki: new Wiki(data.wiki),
            typeahead: data.typeahead as NgOption[],
          };
        }),
      ),
    ).finally(() => this.goIdle());
  }

  /**
   * Handles HTTP errors and returns a standardized error message
   * @param error The HTTP error response
   * @returns Standardized error message
   */
  private handleHttpError(error: HttpErrorResponse): string {
    let errorMessage = 'An error occurred';
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return errorMessage;
  }

  /**
   * Performs an HTTP request with error handling
   * @param request The HTTP request observable
   * @param loader Whether to show loading indicator
   * @returns Promise resolving to the response
   * @throws Error if request fails
   */
  public async performRequest<T>(request: Observable<T>, loader = false): Promise<T> {
    if (loader) {
      this.navigationService.addToWorkQueue();
    }

    try {
      return await firstValueFrom(request);
    } catch (error) {
      const errorMessage = this.handleHttpError(error as HttpErrorResponse);
      console.error('Request failed:', errorMessage);
      throw new Error(errorMessage);
    } finally {
      if (loader) {
        this.goIdle();
      }
    }
  }

  /**
   * Search for similar records using the `record_id`
   * @param {number} record_id - The record to search for similar records
   * @param {boolean} external - Search AgencyBloc only
   * @param {boolean} loader - Show the loading spinner
   * @returns {Promise<SearchResults>}
   */
  public async search(record_id: number, external = false, loader = false): Promise<SearchResults> {
    try {
      const searchPromise = this.performRequest<SearchResults>(
        this.http.post<SearchResults>(`${environment.api_url}/our-data/search`, { record_id, external, scheduling: this.router.url.includes('scheduling') }).pipe(
          catchError((error) => {
            console.error('Search error:', error);
            throw error;
          }),
        ),
        loader,
      );

      return await searchPromise;
    } catch (error) {
      console.error('Search failed:', error);
      throw error;
    }
  }

  /**
   * Fetch the details of a matched record
   * @param {GenericAgencyBlocEntity} record - The record to fetch details for
   * @param {boolean} loader - Show the loading spinner
   * @returns {Promise<Partial<AgencyBlocEntity>>}
   */
  public async matchDetails(record: GenericAgencyBlocEntity, loader = false): Promise<Partial<AgencyBlocEntity>> {
    try {
      const detailsPromise = this.performRequest<Partial<AgencyBlocEntity>>(
        this.http.post<Partial<AgencyBlocEntity>>(`${environment.api_url}/our-data/match-details`, record).pipe(
          catchError((error) => {
            console.error('matchDetails error:', error);
            throw error;
          }),
        ),
        loader,
      );

      return await detailsPromise;
    } catch (error) {
      console.error('matchDetails failed:', error);
      throw error;
    }
  }

  /**
   * Search for all policy records using the page and fragment
   * @param {string} page - The current page
   * @param {string} fragment - the current fragement
   * @returns {Promise<PageFragment>}
   */
  public async searchPolicy(page: string, fragment: string): Promise<PageFragment> {
    return await firstValueFrom(
      this.http.get<PageFragment>(`${environment.api_url}/policies/data/policies/${fragment}`).pipe(
        map((data) => {
          return {
            page,
            fragment,
            fields: data.fields,
            parsed: {},
            wiki: new Wiki(data.wiki),
            typeahead: data.typeahead as NgOption[],
          };
        }),
      ),
    ).finally(() => this.goIdle());
  }

  /**
   * search for all policy records for Policy Holder
   * @param {number} record_id - The current page
   * @returns {Promise<SearchResults>}
   */
  public async otherPoliciesFromPolicyHolder(record_id: number): Promise<SearchResults> {
    return await firstValueFrom(this.http.post<SearchResults>(`${environment.api_url}/policies/search`, { record_id }));
  }

  /**
   * get policy details for PolicyID
   * @param {number} policyID - The current page
   * @returns {Promise<AgencyBlocPolicy>}
   */
  public async getPolicyDetails(policyID: number): Promise<AgencyBlocPolicy> {
    return await firstValueFrom(this.http.get<AgencyBlocPolicy>(`${environment.api_url}/policies/details/${policyID}`));
  }

  /**
   * Sync the given entity with AgencyBloc
   * @param {CreateAgencyBlocEntityFrom} from_type - The type of record we are syncing from
   * @param {CreateAgencyBlocEntityTo} to_type - The type of entity to sync to
   * @param {Partial<AgencyBlocEntity>} record - The payload to send to AgencyBloc
   * @param {boolean} loader - Show the loading spinner
   * @returns {Promise<AgencyBlocEntity>}
   */
  public async syncAgencyBlocRecord(from_type: CreateAgencyBlocEntityFrom, to_type: CreateAgencyBlocEntityTo, record: Partial<AgencyBlocEntity>, loader = false): Promise<AgencyBlocEntity> {
    return await this.performRequest<AgencyBlocEntity>(this.http.post<AgencyBlocEntity>(`${environment.api_url}/our-data/sync`, { from_type, to_type, record }), loader);
  }

  /**
   * Create a new AgencyBloc record
   * @param {number} form_id - The form ID to create the record from
   * @param {CreateAgencyBlocEntityFrom} from_type - The type of record we are creating the record from
   * @param {CreateAgencyBlocEntityTo} to_type - The type of entity to create
   * @param {Partial<AgencyBlocEntity>} record - The payload to send to AgencyBloc
   * @param {boolean} loader - Show the loading spinner
   * @returns {Promise<AgencyBlocEntity>}
   */
  public async createAgencyBlocRecord(
    form_id: number,
    from_type: CreateAgencyBlocEntityFrom,
    to_type: CreateAgencyBlocEntityTo,
    record: Partial<AgencyBlocEntity>,
    loader = false,
  ): Promise<AgencyBlocEntity> {
    return await this.performRequest<AgencyBlocEntity>(this.http.post<AgencyBlocEntity>(`${environment.api_url}/our-data`, { form_id, from_type, to_type, record }), loader);
  }

  /**
   * Create a new Policy record
   * @param {number} entityID - The indivisual id for the record from
   * @param {number} recordId - The indivisual id for the record from
   * @param {string} entityType - The entity type for the record from
   * @param {string} projectCode - The project code for the record from
   * @param {'agent' | 'individual'} entityTypeID - The type of record form
   * @param {string} policyCoverageType - The payload of record form
   * @param {string} policyStatus - The payload of record form
   * @param {string} carrier - The payload of record form
   * @returns {Promise<AgencyBlocEntity>}
   */
  public async createPolicyBlocRecord(
    entityID: number,
    recordId: number,
    entityType: string,
    projectCode: string,
    policyCoverageType: string,
    policyStatus: string,
    carrier: string,
  ): Promise<AgencyBlocEntity> {
    return await this.performRequest<AgencyBlocEntity>(
      this.http.post<AgencyBlocEntity>(`${environment.api_url}/policies`, { entityID, recordId, entityTypeID: entityType, projectCode, policyCoverageType, policyStatus, carrier }),
    );
  }

  /**
   * sync a existing Policy record
   * @param {number} entityID - The policy id for the record from
   * @param {number} recordId - The indivisual id for the record from
   * @param {string} projectCode - The project code for the record from
   * @param {'agent' | 'individual'} entityTypeID - The type of record form
   * @param {string} policyCoverageType - The payload of record form
   * @param {string} policyStatus - The payload of record form
   * @param {string} carrier - The payload of record form
   * @returns {Promise<AgencyBlocEntity>}
   */
  public async syncPolicyBlocRecord(entityID: number, recordId: number, projectCode: string, policyCoverageType: string, policyStatus: string, carrier: string): Promise<AgencyBlocEntity> {
    return await this.performRequest<AgencyBlocEntity>(
      this.http.put<AgencyBlocEntity>(`${environment.api_url}/policies/sync`, { entityID, recordId, projectCode, entityTypeID: 'Policy', policyCoverageType, policyStatus, carrier }),
    );
  }

  /**
   * Updatae a `FormSubmission` record
   * @param {SearchResultsRecord} record - The record data being updated
   * @returns {Promise<SearchResultsRecord>}
   */
  public async update(record: Partial<SearchResultsRecord>, loader = false): Promise<SearchResultsRecord> {
    return await this.performRequest<SearchResultsRecord>(this.http.put<SearchResultsRecord>(`${environment.api_url}/our-data`, record), loader);
  }

  /**
   * Assign a record to a user
   * @param {number[]} record_ids - The record id to assign
   * @param {CreateAgencyBlocEntityFrom} from_type - The type of record we are creating the record from
   * @param {number} agent_id - The AgencyBlocAgent id to assign the record to
   * @param {boolean} urgent - Is the record urgent?
   * @returns {Promise<SearchResultsRecord>}
   */
  public async assignRecordTo(record_ids: number[], from_type: CreateAgencyBlocEntityFrom, payload: Partial<AssignTo>, loader = false) {
    return await this.performRequest<SearchResultsRecord>(this.http.put<SearchResultsRecord>(`${environment.api_url}/our-data/assign`, { record_ids, from_type, ...payload }), loader);
  }

  /**
   * Generate a PDF for the given record
   * @param {SearchResultsRecord} record - The record to generate a PDF for
   * @returns {Promise<GeneratedPdf>}
   */
  public async generatePdf(form: (typeof FormSubmissionNames)[number], data: Partial<FormSubmissionType>, loader = false) {
    return await this.performRequest<GeneratedPdf>(this.http.post<GeneratedPdf>(`${environment.api_url}/form-submission/${form}`, { formData: { applicant: data } }), loader);
  }

  /**
   * Get the files for a given record. Only FormSubmissions have files.
   * @param {number} record_id - The record id to get files for
   * @param {boolean} loader - Show the loading spinner
   * @returns {Promise<string[]>}
   */
  public async getFilesForRecord(record_id: number, loader = false) {
    return await this.performRequest<string[]>(this.http.get<string[]>(`${environment.api_url}/our-data/${record_id}/files`), loader);
  }

  /**
   * Get a record by its `id`
   * @param {number} id - The record id
   * @param {boolean} loader - Show the loading spinner
   * @returns {Promise<SearchResultsRecord>}
   */
  public async getById(id: number, loader = false) {
    // Check if the record is already in the pagination data with a `data` property set.
    const record = this.pagination$.value?.data.find((record) => record.id === id) as SearchResultsRecord | undefined;

    // If the record is already in the pagination data, return it
    if (record && 'data' in record) {
      return record;
    }

    // Otherwise, get the record from the server
    if (loader) this.navigationService.addToWorkQueue();

    // console.log(`Getting record ${id} from the server...`);

    return await firstValueFrom(this.http.get<SearchResultsRecord>(`${environment.api_url}/our-data/${id}`))
      .then((record) => this.updateRecordInPagination(record))
      .finally(() => (loader ? this.goIdle() : void 0));
  }

  /**
   * Update or add a record in the pagination data
   * @param {SearchResultsRecord} record - The record to update
   * @returns {SearchResultsRecord}
   */
  public updateRecordInPagination(record: SearchResultsRecord) {
    // Get the index of the record in the pagination data
    const index = this.pagination$.value?.data.findIndex((x) => record.id === x.id);

    // Get the pagination data
    const pagination = this.pagination$.getValue();

    // If there is no pagination data, throw an error
    if (!pagination) throw new Error('No pagination data found!');

    // If the record is in the pagination data, update it
    if (index !== undefined && index !== -1) {
      pagination.data[index] = record;
    } else {
      // Otherwise, add it to the pagination data
      pagination.data.push(record);
    }

    // Update the pagination data
    this.pagination$.next(pagination);

    return record;
  }

  /**
   * Removes an item from the work queue and any other work that needs to be done when the app is idle
   */
  private goIdle(): void {
    // this.record$.next(undefined);
    this.navigationService.removeFromWorkQueue();
  }

  /**
   * When a record is checked, update `batchTo$`, `pagination$` data, and the check all checkbox
   * @param event The checkbox change event
   * @param record The record being checked/unchecked
   * @param checkbox Optional form checkbox component reference
   */
  public onRecordChange($event: Event, record: SearchResultsRecord, checkbox?: FormCheckboxComponent): void {
    try {
      // Update the pagination data
      const pagination = this.pagination$.value;
      if (pagination) {
        pagination.data = pagination.data.map((x) => ({
          ...x,
          checked: x.id === record.id ? record.checked : x.checked,
        }));

        this.pagination$.next(pagination);
      }

      // Get all the checked records
      const checked = this.pagination$.value?.data.filter((record) => record.checked).map((record) => record.id) ?? [];

      // Are all records checked?
      const allChecked = this.pagination$.value?.data.every((record) => record.checked) ?? false;

      // Update the batchTo$ observable
      this.batchTo$.next(checked);

      // Update the check all checkbox
      this.checkbox = allChecked;

      // Update is_any_checkbox_selected$ state
      this.is_any_checkbox_selected$.next(checked.length > 0);
    } catch (error) {
      console.error('Error in onRecordChange:', error);
      // Handle error appropriately - could emit error state or show user notification
    }
  }

  /**
   * Check all visible records
   */
  public checkAllVisibleRecords(): void {
    this.checkbox = !this.checkbox;
    this.paginatedDataService.checkAllPaginatedData(this.checkbox);
  }

  /**
   * Opens a modal to view a specific record.
   * @param {string} name - The name of the modal component to open.
   * @param {SearchResultsRecord} record - The record to view in the modal.
   * @param {boolean} [fetch=false] - Whether to fetch the latest data for the record before opening the modal.
   * @returns {Promise<void>} - A promise that resolves when the modal has been opened or access is denied.
   */
  public async openModal(name: string, record: SearchResultsRecord, fetch: boolean = false): Promise<void> {
    if (!this.accessable$.value.edit) {
      // alert(`You do not have permission to open the '${name}'.`);
      this.is_not_authorized_modal_open$.next(true);
      return;
    }

    //Hipaa Permission Checking
    const hipaa_modals = ['RecordDetailsComponent', 'FindMatchingRecordsComponent', 'DownloadFilesModal'];
    if (hipaa_modals.includes(name) && !this.featureAuthorizationService.canAccessHipaa()) {
      this.is_not_authorized_modal_open$.next(true);
      return;
    }

    //Hipaa Feature checking
    if (name === 'RecordDetailsComponent' && !this.featureAuthorizationService.canAccessFeature('hipaa|view-forms-data')) {
      this.is_not_authorized_modal_open$.next(true);
      return;
    }

    if (name === 'DownloadFilesModal' && !this.featureAuthorizationService.canAccessFeature('hipaa|download-forms-pdf')) {
      this.is_not_authorized_modal_open$.next(true);
      return;
    }

    // Get the latest record data
    if (fetch) record = await this.getById(record.id);

    // Set the record$ observable to the latest record data
    this.record$.next(record);

    // Open the modal by the component's constructor name
    this.presentByName$.next(name);

    if (name === 'RecordDetailsComponent') {
      this.sidebarService.allow_collapsed$.next(true);
      this.sidebarService.setSidebarState(false);
    } else this.sidebarService.allow_collapsed$.next(false);
  }

  /**
   * Recursive function to get the last segment from the ActivatedRoute
   */
  public getLastSegment(route: ActivatedRoute): string {
    if (route.firstChild) {
      return this.getLastSegment(route.firstChild);
    } else if (route.snapshot?.url.length > 0) {
      return route.snapshot.url[route.snapshot.url.length - 1].path;
    } else {
      return '';
    }
  }

  /**
   * Convert form input key to uppercase with spaces
   * @param {string} inputString - The name of the form field
   */
  public convertKeyToUpperCaseWithSpace(inputString: string): string {
    // Convert camelCase or underscore_case to spaces
    const stringWithSpaces = inputString.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
    const uppercaseString = stringWithSpaces.toUpperCase();
    return uppercaseString;
  }

  /**
   * Update LTCI option for filter
   */
  public async updateLTCIOption() {
    try {
      // Only make API call if ltci_options$ is empty
      if (this.ltci_options$.value.length === 0) {
        const data = await firstValueFrom(this.http.get<AGENTS_TYPEAHEAD[]>(`${environment.api_url}/typeahead/agents`));
        const ltci_specialist_options = data.filter((item) => item.type?.includes('LTCI Specialist'));
        this.ltci_options$.next(ltci_specialist_options);
      }
    } catch (error) {
      console.error('Error fetching LTCI options:', error);
      // Keep existing options in case of error
    }
  }
}

export interface PartialEntityValues<T> {
  entityType?: string;
  id?: number;
}

/**
 * After a PDF is regenerated, this is the data that is returned
 */
export interface GeneratedPdf {
  /**
   * The name of the form
   */
  form_name: string;

  /**
   * The email addresses that were sent the PDF
   */
  mail_to: string[];

  /**
   * The name of the file
   */
  file_name: string;

  /**
   * The PDFs that were generated
   */
  pdfs: any[];
}

export type CreateAgencyBlocEntityFrom = 'meeting' | 'agent' | 'individual' | 'group' | 'policy' | 'form-submission';
export type CreateAgencyBlocEntityTo = 'agents' | 'individuals' | 'groups';
export interface AGENTS_TYPEAHEAD {
  agentID: string;
  label: string;
  type: string;
  value: number | string;
}
